'use client';

import { DateTime } from 'luxon';
import { useTheme } from '@emotion/react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Comment, Reply } from '../../models/comment';
import { Image } from '../picture/picture';
import CheckSVG from '../../public/img/small-check.svg';
import LogoSVG from '../../public/img/logo.svg';
import StarSVG from '../../public/img/star.svg';
import { V3 } from '../../lib/api/urls';
import { http } from '../../lib/api/http';
import { ImageProp } from '../../models/image';

export function CommentItem({
  comment,
  getParent,
  setImages,
  setImagesSlider,
  onOpenCommentFormModal,
}: {
  comment: Comment;
  getParent: (id: number) => Comment | null;
  setImages: Dispatch<SetStateAction<ImageProp[]>>;
  setImagesSlider: Dispatch<
    SetStateAction<{ slide: number | null; visible: boolean }>
  >;
  onOpenCommentFormModal: (reply: Comment) => void;
}) {
  const theme = useTheme();
  const [replies, setReplies] = useState<Reply | null>(null);

  const getDate = (time: string) =>
    DateTime.fromISO(time).setLocale('ru').toFormat('dd MMMM yyyy');

  const getName = (item: Comment) => {
    if (item?.is_admin) {
      return 'Администратор';
    }
    if (item?.name) {
      return `${item.name}`;
    }
    if (item?.author?.first_name) {
      return `${item.author.first_name} ${item.author.last_name}`;
    }
    return 'Гость';
  };

  const getReplyName = () => {
    const parent = getParent(Number(comment.parent));
    if (parent) {
      return getName(parent);
    }
    return '';
  };

  const getStateOfComment = (status: string) => {
    if (status === 'new') {
      return 'На модерации';
    }
    if (status === 'inactive') {
      return 'Не опубликовано';
    }
    return '';
  };

  const loadMoreReplies = async () => {
    if (replies) {
      const result = await http.get(replies.next || '', {}, null, {
        contentType: 'json',
        withTokenRequest: true,
      });
      setReplies(result);
    }
  };

  const handleImageClick = (id: number) => {
    if (comment.images.length) {
      setImages(comment.images);
      setImagesSlider({ slide: id, visible: true });
    }
  };

  useEffect(() => {
    const fetch = async () => {
      const commentReplies: Reply = await http.get(
        V3.commentsChildren(comment.id),
        { limit: 3, with_inactive: 'true' },
        null,
        {
          contentType: 'json',
          withTokenRequest: true,
        }
      );
      setReplies(commentReplies);
    };
    fetch();
  }, [comment.id, comment.children_count]);

  return (
    <div
      className='comment__item'
      css={{
        background: '#fff',
        marginBottom: 10,
        display: 'flex',
        gap: 20,
        borderRadius: 4,
        padding: '20px 20px 50px 20px',
        boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.1)',
      }}
    >
      <div className='comment__item-avatar'>
        {comment.is_admin ? (
          <LogoSVG css={{ width: 50, height: 50 }} />
        ) : (
          <div
            css={{
              width: 50,
              height: 50,
              borderRadius: '100%',
              backgroundImage: `url(${
                comment.author?.avatar || '/img/user-no-photo.png'
              })`,
              backgroundSize: 'cover',
            }}
          />
        )}
      </div>
      <div css={{ width: '100%' }}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            width: '100%',
            gap: 4,
          }}
        >
          <div css={{ fontSize: 14, fontWeight: 700, marginBottom: 5 }}>
            {comment.is_admin && (
              <CheckSVG css={{ width: 10, height: 7, marginRight: 5 }} />
            )}
            {getName(comment)}
          </div>
          <div
            css={{
              color: theme.colors.grayStrong,
              whiteSpace: 'nowrap',
              marginBottom: 4,
            }}
          >
            {getStateOfComment(comment.status)}
          </div>
        </div>
        {!!comment.parent && !!getReplyName() && (
          <p>в ответ {getReplyName()}</p>
        )}

        {!!comment.rating && (
          <span css={{ display: 'flex', gap: 5, marginBottom: 10 }}>
            {[1, 2, 3, 4, 5].slice(0, comment.rating).map((ind) => (
              <StarSVG
                key={ind}
                css={{ width: 11, height: 11, fill: '#FFC907' }}
              />
            ))}
            {[1, 2, 3, 4, 5].slice(0, 5 - comment.rating).map((ind) => (
              <StarSVG
                key={ind}
                css={{ width: 11, height: 11, fill: '#bfc1c3' }}
              />
            ))}
          </span>
        )}

        {!!comment.visited_at && (
          <p
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              marginBottom: 10,
            }}
          >
            <CheckSVG css={{ width: 10, height: 7 }} />
            <span>Пользователь посещал мероприятие</span>
          </p>
        )}
        <p css={{ fontSize: 12, color: '#7D7B7A', marginBottom: 10 }}>
          {!!comment.created_at && getDate(comment.created_at)}
        </p>

        <p css={{ marginBottom: 10 }}>{comment.description}</p>

        <div
          css={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}
        >
          {comment.images.map((item, i) => (
            <div onClick={() => handleImageClick(i)} key={item.id}>
              <Image
                image={item.image}
                css={{
                  width: 87,
                  height: 69,
                  cursor: 'pointer',
                  objectFit: 'cover',
                }}
              />
            </div>
          ))}
        </div>

        <button
          css={{
            fontSize: 15,
            fontWeight: 600,
            color: theme.colors.primary,
            cursor: 'pointer',
          }}
          onClick={() => onOpenCommentFormModal(comment)}
          type='button'
        >
          Ответить
        </button>

        {/* admin description */}
        {comment.admin_description ? (
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
              gap: 20,
              marginTop: 20,
            }}
          >
            <div>
              <LogoSVG css={{ width: 50, height: 50 }} />
            </div>
            <div>
              <div css={{ fontSize: 14, fontWeight: 700, marginBottom: 5 }}>
                <CheckSVG css={{ width: 10, height: 7, marginRight: 5 }} />
                Ответ Администратора
              </div>
              <p css={{ marginTop: 10 }}>{comment.admin_description}</p>
            </div>
          </div>
        ) : (
          ''
        )}

        {!!replies?.results?.length && (
          <div
            css={{
              marginTop: 20,
              '.comment__item': {
                padding: 'unset',
                boxShadow: 'unset',
              },
              '.comment__item-avatar svg, .comment__item-avatar div': {
                width: 36,
                height: 36,
              },
            }}
          >
            {replies.results.map((item: Comment) => (
              <CommentItem
                key={comment.id}
                comment={item}
                getParent={getParent}
                setImagesSlider={setImagesSlider}
                setImages={setImages}
                onOpenCommentFormModal={onOpenCommentFormModal}
              />
            ))}

            {replies.count > replies.results.length && (
              <button
                css={{
                  background: '#dfdfdf',
                  border: '2px solid #dfdfdf',
                  fontSize: 16,
                  borderRadius: 30,
                  padding: '8px 26px',
                  marginTop: 10,
                }}
                type='button'
                onClick={loadMoreReplies}
              >
                Показать еще ответы
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
